/**
 * quotePrerequisites.ts
 * Frontend service for the quote prerequisite-status endpoint.
 *
 * Phase 5, Item 2 — Quote line prerequisite chips.
 */

import apiClient from './apiClient';

// ── Types ──────────────────────────────────────────────────────────────────

export interface PrereqDetail {
  requiredTestCatalogItemKey: string;
  requiredTestName: string;
  minSkillLevel: number | null;
  isOnQuote: boolean;
}

export interface PrereqStatusRow {
  lineItemKey: string;
  testCatalogItemKey: string | null;
  testName: string | null;
  prerequisites: PrereqDetail[];
}

// ── Pure helper ────────────────────────────────────────────────────────────

/**
 * Formats a prerequisite chip label.
 * If minSkillLevel is set, prepends "[L{n}] ".
 *
 * Examples:
 *   formatPrereqChipLabel({ requiredTestName: 'Sieve Analysis', minSkillLevel: null }) → 'Sieve Analysis'
 *   formatPrereqChipLabel({ requiredTestName: 'Sieve Analysis', minSkillLevel: 3 })    → '[L3] Sieve Analysis'
 */
export function formatPrereqChipLabel(prereq: Pick<PrereqDetail, 'requiredTestName' | 'minSkillLevel'>): string {
  if (prereq.minSkillLevel != null) {
    return `[L${prereq.minSkillLevel}] ${prereq.requiredTestName}`;
  }
  return prereq.requiredTestName;
}

// ── API call ───────────────────────────────────────────────────────────────

/**
 * Fetches prerequisite status for all line items on the given quote.
 * Returns one row per line item; each row lists all prereqs with isOnQuote flag.
 */
export async function getQuotePrerequisiteStatus(quoteKey: string): Promise<PrereqStatusRow[]> {
  const response = await apiClient.get<PrereqStatusRow[]>(`/quotes/${quoteKey}/prerequisite-status`);
  return response.data;
}
